import React, { useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { PROYECTOS } from '../constants/testIds'
import { useSiteContent } from '../contexts/SiteContentContext'

const BF = 'Anton, sans-serif'
const MONO = 'JetBrains Mono, monospace'
const SG = 'Space Grotesk, sans-serif'

function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  )
}

function RevealLine({ children, delay = 0 }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <div ref={ref} style={{ overflow: 'hidden', paddingTop: '0.1em', marginTop: '-0.1em', paddingBottom: '0.06em', marginBottom: '-0.06em' }}>
      <motion.div initial={{ y: '105%' }} animate={inView ? { y: 0 } : {}}
        transition={{ delay, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}>
        {children}
      </motion.div>
    </div>
  )
}

/* ── Modal ──────────────────────────────────────────────────── */
function ProjectModal({ project, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  return createPortal(
    <motion.div
      data-testid={`project-modal-${project.id}`}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 flex flex-col"
      style={{ zIndex: 9999, backgroundColor: '#000' }}
    >
      <motion.div
        data-lenis-prevent
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="flex-1 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {/* Top bar */}
        <div className="sticky top-0 bg-black flex justify-between items-center px-5 md:px-14 pt-7 pb-5 border-b border-neutral-900" style={{ zIndex: 10 }}>
          <div className="flex items-center gap-4">
            <span style={{ fontFamily: MONO, fontSize: '11px', color: '#F5A623', letterSpacing: '0.3em' }}>
              {project.num}
            </span>
            <span className="text-white/30 text-xs uppercase tracking-widest" style={{ fontFamily: MONO }}>
              {project.category}
            </span>
          </div>
          <button
            onClick={onClose}
            data-testid={`close-modal-${project.id}`}
            className="flex items-center justify-center bg-transparent border border-neutral-700 hover:border-white hover:text-white transition-all duration-200"
            style={{ width: 40, height: 40, color: 'rgba(255,255,255,0.7)', fontSize: 20, lineHeight: 1, cursor: 'pointer', flexShrink: 0 }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Browser mockup with full screenshot */}
        <div className="px-5 md:px-14 mt-8 mb-8">
          <div className="border border-neutral-800 overflow-hidden" style={{ borderRadius: '6px' }}>
            {/* Browser chrome bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-neutral-950 border-b border-neutral-800">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-neutral-700" />
                <div className="w-3 h-3 rounded-full bg-neutral-700" />
                <div className="w-3 h-3 rounded-full bg-neutral-700" />
              </div>
              <div className="flex-1 mx-3 bg-neutral-900 rounded px-3 py-1">
                <span className="text-white/30 text-xs" style={{ fontFamily: MONO }}>
                  {project.url ? project.url.replace('https://', '') : project.id}
                </span>
              </div>
            </div>
            {/* Full screenshot — NO height limit, image shows complete */}
            <div className="w-full overflow-hidden bg-neutral-950">
              <img
                src={project.modalImage || project.modal_image || project.image}
                alt={project.title}
                className="w-full"
                style={{ display: 'block', height: 'auto', maxHeight: '65vh', objectFit: 'cover', objectPosition: 'top' }}
                onError={e => { e.target.src = project.image }}
              />
            </div>
          </div>
        </div>

        {/* Info + CTA */}
        <div className="px-5 md:px-14 pb-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20">
          <div>
            <h2 className="uppercase text-white mb-2"
              style={{ fontFamily: BF, fontSize: 'clamp(32px, 4vw, 64px)', letterSpacing: '0.01em', lineHeight: 1 }}>
              {project.title}
            </h2>
            <p className="text-white/30 text-xs tracking-widest uppercase mb-6" style={{ fontFamily: MONO }}>
              {project.subcategory} · {project.year}
            </p>
            <p className="text-white/60 leading-relaxed mb-10" style={{ fontFamily: SG, fontSize: 'clamp(14px, 1.1vw, 17px)' }}>
              {project.longDescription || project.long_description}
            </p>

            {/* CTA primary */}
            {!project.noUrl && !project.no_url && project.url && (
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`project-link-${project.id}`}
                className="inline-flex items-center gap-3 px-6 py-4 bg-amber-400 hover:bg-amber-300 transition-colors duration-200 group"
                style={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '0.1em', color: '#000', textDecoration: 'none', whiteSpace: 'nowrap' }}
              >
                <span className="uppercase font-medium">Ver proyecto en vivo</span>
                <span className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200">↗</span>
              </a>
            )}
          </div>

          <div>
            <div className="text-white/25 text-xs tracking-[0.3em] uppercase mb-5" style={{ fontFamily: MONO }}>
              Servicios incluidos
            </div>
            <div className="flex flex-col gap-0">
              {(project.tech || []).map((t, i) => (
                <div key={t} className="flex items-center gap-4 py-4 border-b border-neutral-900 first:border-t first:border-neutral-900">
                  <span className="text-amber-400/60" style={{ fontFamily: MONO, fontSize: '10px' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-white/70" style={{ fontFamily: SG, fontSize: '15px' }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  )
}

/* ── Project Card ──────────────────────────────────────────── */
function ProjectCard({ project, index, onOpen }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  const [hovered, setHovered] = useState(false)
  const [imgSrc, setImgSrc] = useState(project.image)

  return (
    <motion.article
      ref={ref}
      data-testid={PROYECTOS.card(project.id)}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(project)}
    >
      {/* Screenshot card */}
      <div className="relative overflow-hidden bg-neutral-950"
        style={{ aspectRatio: '16/10', border: `1px solid ${hovered ? '#F5A623' : '#1a1a1a'}`, transition: 'border-color 0.3s ease' }}>

        {/* Screenshot image — aspect ratio maintained, no cut */}
        <img
          src={imgSrc}
          alt={project.title}
          className="w-full h-full"
          style={{
            objectFit: 'cover',
            objectPosition: 'top',
            display: 'block',
            transform: hovered ? 'scale(1.04)' : 'scale(1)',
            filter: hovered ? 'brightness(0.45)' : 'brightness(0.8)',
            transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1), filter 0.4s ease',
          }}
          onError={() => setImgSrc('https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80')}
        />

        {/* Hover overlay CTA */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4"
          style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.3s ease' }}>
          <span
            className="px-6 py-3 border border-white/50 text-white text-xs tracking-[0.3em] uppercase"
            style={{ fontFamily: MONO, backgroundColor: 'rgba(0,0,0,0.3)' }}>
            Ver detalles ↗
          </span>
        </div>

        {/* Number badge */}
        <div className="absolute top-4 left-4">
          <span style={{ fontFamily: MONO, fontSize: '11px', color: '#F5A623', letterSpacing: '0.2em' }}>
            {project.num}
          </span>
        </div>

        {/* Category badge */}
        <div className="absolute top-4 right-4">
          <span className="px-2 py-1 text-white/70 text-xs uppercase tracking-widest"
            style={{ fontFamily: MONO, fontSize: '9px', background: 'rgba(0,0,0,0.6)' }}>
            {project.year}
          </span>
        </div>
      </div>

      {/* Card footer */}
      <div className="pt-4 pb-6">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h3 className="text-white uppercase leading-none"
            style={{ fontFamily: BF, fontSize: 'clamp(20px, 2vw, 28px)', letterSpacing: '0.01em' }}>
            {project.title}
          </h3>
          <span className="text-amber-400/80 flex-shrink-0 mt-1"
            style={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '0.15em' }}>
            {project.category.toUpperCase()}
          </span>
        </div>
        <p className="text-white/30 text-xs uppercase tracking-widest mb-3" style={{ fontFamily: MONO }}>
          {project.subcategory}
        </p>
        <p className="text-white/50 leading-relaxed" style={{ fontFamily: SG, fontSize: '14px', lineHeight: '1.6' }}>
          {project.description}
        </p>
      </div>
    </motion.article>
  )
}

/* ── Section ────────────────────────────────────────────────── */
export default function Proyectos() {
  const [activeProject, setActiveProject] = useState(null)
  const { content } = useSiteContent()

  const normProjects = (content.projects || []).map(p => ({
    ...p,
    longDescription: p.longDescription || p.long_description || '',
    modalImage: p.modalImage || p.modal_image || p.image || '',
    noUrl: p.noUrl ?? p.no_url ?? false,
  }))

  return (
    <>
      <section id="proyectos" data-testid={PROYECTOS.section} className="bg-black py-24 md:py-36">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 xl:px-16">

          {/* Label */}
          <FadeIn delay={0}>
            <div className="flex items-center gap-4 mb-12">
              <span className="accent-rule" />
              <span className="text-xs tracking-[0.35em] text-amber-400 uppercase" style={{ fontFamily: MONO }}>
                Proyectos
              </span>
            </div>
          </FadeIn>

          {/* Heading */}
          <div className="text-center mb-6">
            <RevealLine delay={0.1}>
              <h2 className="uppercase text-white leading-none"
                style={{ fontFamily: BF, fontSize: 'clamp(48px, 8vw, 120px)', letterSpacing: '0.01em' }}>
                LO QUE
              </h2>
            </RevealLine>
            <RevealLine delay={0.22}>
              <h2 className="uppercase leading-none"
                style={{ fontFamily: BF, fontSize: 'clamp(48px, 8vw, 120px)', letterSpacing: '0.01em', color: '#F5A623' }}>
                PUEDO
              </h2>
            </RevealLine>
            <RevealLine delay={0.34}>
              <h2 className="uppercase text-white leading-none"
                style={{ fontFamily: BF, fontSize: 'clamp(48px, 8vw, 120px)', letterSpacing: '0.01em' }}>
                HACER.
              </h2>
            </RevealLine>
          </div>

          <FadeIn delay={0.4} className="text-center mb-16">
            <p className="text-white/40 max-w-sm mx-auto" style={{ fontFamily: SG, fontSize: '14px' }}>
              Proyectos reales — el nivel que puedo aportar a tu negocio.
            </p>
          </FadeIn>

          {/* Grid: 1 col mobile / 2 col tablet / 3 col desktop */}
          <div data-testid={PROYECTOS.grid}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
            {normProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
                onOpen={setActiveProject}
              />
            ))}
          </div>

        </div>
      </section>

      <AnimatePresence>
        {activeProject && (
          <ProjectModal project={activeProject} onClose={() => setActiveProject(null)} />
        )}
      </AnimatePresence>
    </>
  )
}
